/**
 * Turns a choice in the "Include a plant stand?" row into a real Planter selection.
 *
 * The stand row is a second view of the Planter option, not a fourth option: each of
 * its radios carries the option value id of the Planter value that pairs the selected
 * planter with that stand ("Pallas with Dark Bamboo Stand"). Rather than teach
 * VariantPicker about a dimension the product doesn't have, this repoints the checked
 * Planter radio at that value and asks the picker to re-resolve, so variant lookup,
 * pricing and the `?variant` URL all keep running through the existing path.
 *
 * The row deliberately lives outside a `<fieldset>`: VariantPicker collects the
 * selected option values with `fieldset input:checked`, so a fieldset here would send
 * two Planter ids and break the lookup.
 */
class PlantStandPicker extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.#handleChange);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#handleChange);
  }

  /** @param {Event} event */
  #handleChange = (event) => {
    const stand = event.target;
    if (!(stand instanceof HTMLInputElement) || stand.type !== 'radio') return;

    const { optionValueId, variantId } = stand.dataset;
    if (!optionValueId) return;

    const picker = this.closest('variant-picker');
    const planterRadio = picker?.querySelector(
      `fieldset[data-option-name="${this.dataset.optionName}"] input[type="radio"]:checked`
    );

    if (!(planterRadio instanceof HTMLInputElement)) return;
    if (typeof (/** @type {any} */ (picker).reselectOptionValue) !== 'function') return;

    // Keep the picker's own listener from seeing this change: it would build a request
    // from the Planter value that is still checked, i.e. the old stand.
    event.stopPropagation();

    planterRadio.dataset.optionValueId = optionValueId;
    if (variantId && variantId !== '-') {
      planterRadio.dataset.variantId = variantId;
    } else {
      delete planterRadio.dataset.variantId;
    }

    /** @type {any} */ (picker).reselectOptionValue(planterRadio, optionValueId);
  };
}

if (!customElements.get('plant-stand-picker')) {
  customElements.define('plant-stand-picker', PlantStandPicker);
}
